/**
 * The English (`en`) message catalog and the derived message-id type.
 *
 * This module is the SINGLE SOURCE OF TRUTH for the set of message ids the
 * frontend uses. The `en` catalog lists every id -> English text; the
 * {@link MessageId} union used across the app is derived from its keys, so:
 *   - a screen referencing an unknown id is a compile-time error, and
 *   - any additional locale catalog is required to cover exactly this id set.
 *
 * Ids are namespaced by screen/area so later screen tasks (15.x) can find the
 * text they need without guessing. The auth-screen namespaces below intentionally
 * cover ALL user-facing text (labels, prompts, and User_Messages) for the
 * registration, email-verification, login, MFA-enrollment, and MFA-challenge
 * screens — this is the complete substitution surface Property 37 (task 14.5)
 * asserts against, and what tasks 15.x consume.
 *
 * User_Message ids carry the what/why/next-action content the UserMessage
 * component (task 14.6, Req 26) renders; keeping them in the catalog means the
 * message text is translatable like everything else (Req 22.11).
 */

/**
 * The English catalog. `as const` so the object's keys form a precise literal
 * type from which {@link MessageId} is derived.
 */
export const EN_MESSAGES = {
  // ---- Shell / generic ---------------------------------------------------
  "app.name": "AVOS",
  "app.nav.primaryLabel": "Primary",
  "app.skipToContent": "Skip to main content",
  "app.loading": "Loading…",

  // ---- Generic User_Messages (what/why/next-action) ----------------------
  // Reused across screens for common conditions. The "next action" default
  // matches the UserMessage contract's fallback (Req 26.1).
  "message.generic.noActionRequired": "No action required — contact support or wait.",
  "message.generic.unexpectedError.what": "Something went wrong.",
  "message.generic.unexpectedError.why": "The request could not be completed.",
  "message.generic.unexpectedError.next": "Try again, or contact support if it persists.",
  "message.generic.networkError.what": "Could not reach the server.",
  "message.generic.networkError.why": "Your device appears to be offline or the connection failed.",
  "message.generic.networkError.next": "Check your connection and try again.",
  "message.field.required": "This field is required.",

  // ---- Registration screen (Req 22.1–22.3) -------------------------------
  "auth.register.title": "Create your account",
  "auth.register.prompt": "Enter your email and choose a password to get started.",
  "auth.register.emailLabel": "Email address",
  "auth.register.emailPlaceholder": "you@example.com",
  "auth.register.passwordLabel": "Password",
  "auth.register.passwordPlaceholder": "Create a password",
  "auth.register.submit": "Create account",
  "auth.register.haveAccountPrompt": "Already have an account?",
  "auth.register.signInLink": "Sign in",
  // Field-level validation User_Messages (Req 22.2).
  "auth.register.emailEmpty": "Enter your email address to continue.",
  "auth.register.emailInvalid": "Enter a valid email address (for example, you@example.com).",
  "auth.register.passwordEmpty": "Enter a password to continue.",
  // Success User_Message (Req 22.3).
  "auth.register.verifyEmailSent.what": "Check your email to verify your account.",
  "auth.register.verifyEmailSent.why": "We sent a verification link to your email address.",
  "auth.register.verifyEmailSent.next": "Open the link within 24 hours to finish signing up.",

  // ---- Email verification screen (Req 22.4–22.5) -------------------------
  "auth.verify.title": "Verify your email",
  "auth.verify.prompt": "Enter the verification code from your email.",
  "auth.verify.tokenLabel": "Verification code",
  "auth.verify.submit": "Verify email",
  "auth.verify.requestNew": "Request a new verification email",
  "auth.verify.success.what": "Your email is verified.",
  "auth.verify.success.why": "You can now access your account.",
  "auth.verify.success.next": "Continue to sign in.",
  "auth.verify.expiredInvalid.what": "That verification link did not work.",
  "auth.verify.expiredInvalid.why": "The code is expired, malformed, or does not match your account.",
  "auth.verify.expiredInvalid.next": "Request a new verification email and try again.",

  // ---- Login screen (Req 23) --------------------------------------------
  // Email + password sign-in ONLY. No third-party / social sign-in and no
  // public sign-up: the catalog deliberately carries no Google/registration ids.
  "auth.login.title": "Welcome back",
  "auth.login.prompt": "Sign in to continue to AVOS.",
  "auth.login.emailLabel": "Email address",
  "auth.login.passwordLabel": "Password",
  "auth.login.submit": "Sign in",
  "auth.login.submitBusy": "Signing in\u2026",
  "auth.login.showPassword": "Show password",
  "auth.login.hidePassword": "Hide password",
  "auth.login.forgotPassword": "Forgot password?",
  // Marketing panel (desktop two-column auth layout). Decorative/brand copy.
  "auth.login.brandEndorsement": "Hospitality Operating System",
  "auth.login.marketingHeadline": "Run every stay from one place.",
  "auth.login.marketingBody": "Bookings, operations, revenue and guest experience, connected in one hospitality operating system.",
  "auth.login.marketingFooter": "One platform. Every stay.",
  "auth.login.emailRequired.what": "Enter your email address.",
  "auth.login.emailRequired.why": "An email address is required to sign in.",
  "auth.login.emailRequired.next": "Type the email you registered with, then sign in.",
  "auth.login.emailInvalid.what": "That email address looks incomplete.",
  "auth.login.emailInvalid.why": "The value entered is not a valid email address.",
  "auth.login.emailInvalid.next": "Check for typos (for example name@example.com) and try again.",
  "auth.login.passwordRequired.what": "Enter your password.",
  "auth.login.passwordRequired.why": "A password is required to sign in.",
  "auth.login.passwordRequired.next": "Type your password, then sign in.",
  "auth.login.invalidCredentials.what": "Sign-in failed.",
  "auth.login.invalidCredentials.why": "The email or password is incorrect.",
  "auth.login.invalidCredentials.next": "Check your details and try again.",
  "auth.login.emailUnverified.what": "Your email is not verified yet.",
  "auth.login.emailUnverified.why": "Tenant data access requires a verified email.",
  "auth.login.emailUnverified.next": "Verify your email, then sign in again.",
  "auth.login.accountLocked.what": "Your account is temporarily locked.",
  "auth.login.accountLocked.why": "There were too many failed sign-in attempts.",
  "auth.login.accountLocked.next": "Wait 15 minutes and try again.",
  "auth.login.accountSuspended.what": "This account is not available.",
  "auth.login.accountSuspended.why": "Your account or organisation has been suspended.",
  "auth.login.accountSuspended.next": "Contact your administrator or AVOS support for help.",
  "auth.login.serverError.what": "We could not sign you in.",
  "auth.login.serverError.why": "The sign-in service is temporarily unavailable.",
  "auth.login.serverError.next": "Wait a moment and try again. If it continues, contact support.",

  // ---- MFA challenge screen (Req 10) -------------------------------------
  "auth.mfa.title": "Enter your verification code",
  "auth.mfa.prompt": "Enter the 6-digit code from your authenticator app.",
  "auth.mfa.codeLabel": "Authentication code",
  "auth.mfa.submit": "Verify",
  "auth.mfa.invalid.what": "That code is not valid.",
  "auth.mfa.invalid.why": "The authentication code did not match.",
  "auth.mfa.invalid.next": "Enter the current code from your authenticator app.",
  "auth.mfa.tooManyAttempts.what": "Too many incorrect codes.",
  "auth.mfa.tooManyAttempts.why": "You entered 5 invalid codes in a row.",
  "auth.mfa.tooManyAttempts.next": "Restart sign-in to try again.",

  // ---- MFA enrollment screen (Req 10.1) ----------------------------------
  "auth.mfaEnroll.title": "Set up two-factor authentication",
  "auth.mfaEnroll.prompt": "Scan the QR code with your authenticator app, then enter the code it shows.",
  "auth.mfaEnroll.codeLabel": "Authentication code",
  "auth.mfaEnroll.submit": "Enable two-factor authentication",
  "auth.mfaEnroll.success.what": "Two-factor authentication is enabled.",
  "auth.mfaEnroll.success.why": "Your account now requires a second factor at sign-in.",
  "auth.mfaEnroll.success.next": "Keep your authenticator app to sign in next time.",

  // ---- Role management screen (Req 23) -----------------------------------
  "admin.roles.title": "Team roles",
  "admin.roles.prompt": "View your team and change each person's role.",
  // Table / list headers and per-row labels.
  "admin.roles.userHeader": "User",
  "admin.roles.currentRoleHeader": "Current role",
  "admin.roles.newRoleHeader": "New role",
  "admin.roles.actionHeader": "Action",
  "admin.roles.roleSelectLabel": "Select a new role for {user}",
  "admin.roles.assign": "Assign role",
  "admin.roles.confirmPrompt": "Assign the {role} role to {user}?",
  "admin.roles.confirm": "Confirm role change",
  "admin.roles.cancel": "Cancel",
  // Role display names (translatable labels for the four RBAC roles, Req 23.3).
  "admin.roles.role.Owner": "Owner",
  "admin.roles.role.Manager": "Manager",
  "admin.roles.role.Staff": "Staff",
  "admin.roles.role.Accountant": "Accountant",
  // Restricted User_Message for non-Owners (Req 23.2).
  "admin.roles.restricted.what": "This screen is restricted to the Owner role.",
  "admin.roles.restricted.why": "Only an Owner may view and change team roles.",
  "admin.roles.restricted.next": "Ask an Owner to make role changes for you.",
  // Success User_Message after a confirmed change the Platform accepted (Req 23.4).
  "admin.roles.assignSuccess.what": "Role updated.",
  "admin.roles.assignSuccess.why": "{user} is now assigned the {role} role.",
  "admin.roles.assignSuccess.next": "No further action is needed.",

  // ---- Support-impersonation consent screen (Owner-only, Req 24) ---------
  // Owner grants/revokes a stored, time-boxed (1–60 min) consent for Support
  // impersonation. All text is resolved through the catalog (Req 22.11); the
  // User_Messages carry the what/why/next-action content (Req 26).
  "admin.impersonation.title": "Support access",
  "admin.impersonation.prompt":
    "Grant time-boxed consent for Support to act within your tenant, or revoke it at any time.",
  "admin.impersonation.statusActive": "Support access is currently active.",
  "admin.impersonation.statusInactive": "Support access is currently inactive.",
  // Grant controls (Req 24.1, 24.2).
  "admin.impersonation.timeBoxLabel": "Time-box (minutes)",
  "admin.impersonation.timeBoxHint": "Choose between 1 and 60 minutes.",
  "admin.impersonation.grant": "Grant support access",
  "admin.impersonation.revoke": "Revoke support access",
  // Client-side time-box range validation (Req 24.2: 1–60 minutes).
  "admin.impersonation.invalidRange.what": "That time-box is out of range.",
  "admin.impersonation.invalidRange.why":
    "The time-box must be a whole number of minutes between 1 and 60.",
  "admin.impersonation.invalidRange.next":
    "Enter a value from 1 to 60 minutes and try again.",
  // Grant success/failure (Req 24.3, 24.4).
  "admin.impersonation.grantSuccess.what": "Support access is active.",
  "admin.impersonation.grantSuccess.why":
    "You granted consent for Support to act within your tenant.",
  "admin.impersonation.grantSuccess.next":
    "Access expires at {expiresAt}. Revoke it sooner from this screen if needed.",
  "admin.impersonation.grantFailed.what": "Support access was not granted.",
  "admin.impersonation.grantFailed.why":
    "The Platform did not accept the consent request.",
  "admin.impersonation.grantFailed.next":
    "Try again, or contact support if the problem continues.",
  // Revoke success/failure (Req 24.5, 24.6).
  "admin.impersonation.revokeSuccess.what": "Support access is revoked.",
  "admin.impersonation.revokeSuccess.why":
    "You withdrew consent for Support to act within your tenant.",
  "admin.impersonation.revokeSuccess.next":
    "Grant it again from this screen whenever you need support.",
  "admin.impersonation.revokeFailed.what": "Support access was not revoked.",
  "admin.impersonation.revokeFailed.why":
    "The Platform did not accept the revocation request.",
  "admin.impersonation.revokeFailed.next":
    "Try again, or contact support if the problem continues.",
  // Non-Owner restriction (Req 24.8).
  "admin.impersonation.restricted.what": "This screen is restricted.",
  "admin.impersonation.restricted.why":
    "Only the Owner role can manage support access for this tenant.",
  "admin.impersonation.restricted.next":
    "Ask your account Owner to manage support access.",

  // ---- Data-export screen (Owner-only, Req 25.1–25.4, 25.8; Req 17) -------
  // Owner requests a full, tenant-scoped, machine-readable export built
  // asynchronously and announced when ready. All text resolves through the
  // catalog (Req 22.11); the User_Messages carry what/why/next-action (Req 26).
  "admin.export.title": "Export your data",
  "admin.export.prompt":
    "Request a full export of your tenant's data. We prepare it in the background and let you know when it is ready.",
  "admin.export.request": "Request data export",
  "admin.export.retrieve": "Retrieve export",
  // Per-request status line (Req 25.2).
  "admin.export.statusNone": "No export has been requested yet.",
  "admin.export.statusRequested": "Your export has been requested and is being prepared.",
  "admin.export.statusAvailable": "Your export is ready to retrieve.",
  // Request accepted / export requested (Req 25.2, Req 17.4).
  "admin.export.requested.what": "Export requested.",
  "admin.export.requested.why":
    "We are preparing a full export of your tenant's data.",
  "admin.export.requested.next":
    "You will be notified when the export is ready to retrieve.",
  // Export available (Req 25.2).
  "admin.export.available.what": "Your export is available.",
  "admin.export.available.why": "The full export of your tenant's data is ready.",
  "admin.export.available.next": "Use the retrieve control to download your export.",
  // Export could not be completed / request denied or failed (Req 25.3, 25.4, 17.5).
  "admin.export.failed.what": "Your export was not produced.",
  "admin.export.failed.why":
    "The Platform could not complete the data export.",
  "admin.export.failed.next":
    "Try requesting the export again, or contact support if the problem continues.",
  // Non-Owner restriction (Req 25.8).
  "admin.export.restricted.what": "This screen is restricted to the Owner role.",
  "admin.export.restricted.why": "Only an Owner may export the tenant's data.",
  "admin.export.restricted.next": "Ask your account Owner to request a data export.",

  // ---- Account-deletion screen (Owner-only, Req 25.5–25.8; Req 18) --------
  // Owner requests deletion after seeing retention consequences and confirming
  // irreversibility. All text resolves through the catalog (Req 22.11); the
  // User_Messages carry what/why/next-action (Req 26).
  "admin.deletion.title": "Delete your account",
  "admin.deletion.prompt":
    "Request deletion of your account. Personal data is erased except records the Platform must retain to meet a legal obligation.",
  "admin.deletion.consequences":
    "The following categories of records are retained under a retention obligation and are not erased by this request:",
  "admin.deletion.confirm":
    "I understand that deleting my account is irreversible.",
  "admin.deletion.submit": "Delete my account",
  // Completion / success, restating retained categories (Req 25.6, Req 18.5).
  "admin.deletion.success.what": "Account deletion requested.",
  "admin.deletion.success.why":
    "The Platform accepted your request and erased your erasable personal data.",
  "admin.deletion.success.next":
    "No further action is required. Records under a retention obligation are kept until that obligation expires.",
  "admin.deletion.success.retainedHeading":
    "Categories of records retained under a retention obligation:",
  // Blocked / denied / failed deletion (Req 25.7, Req 18.2 RETENTION_STATUS_UNKNOWN).
  "admin.deletion.blocked.what": "Your account was not deleted.",
  "admin.deletion.blocked.why":
    "The retention status of one or more records could not be determined, so no data was erased.",
  "admin.deletion.blocked.next":
    "No action is required now. Deletion resumes once every record's retention status is verified.",
  "admin.deletion.failed.what": "Your account was not deleted.",
  "admin.deletion.failed.why": "The Platform did not accept the deletion request.",
  "admin.deletion.failed.next":
    "Try again, or contact support if the problem continues.",
  // Non-Owner restriction (Req 25.8).
  "admin.deletion.restricted.what": "This screen is restricted to the Owner role.",
  "admin.deletion.restricted.why": "Only an Owner may delete the tenant's account.",
  "admin.deletion.restricted.next": "Ask your account Owner to manage account deletion.",
} as const;

/**
 * The precise set of known message ids, derived from the English catalog keys.
 * Every locale catalog is typed against this union, so translations must cover
 * exactly this set (no missing, no extra).
 */
export type KnownMessageId = keyof typeof EN_MESSAGES;
